import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { 
  Users, 
  Wallet, 
  Link as LinkIcon, 
  Copy, 
  Check, 
  CurrencyDollar,
  UserPlus,
  Hourglass,
  CheckCircle,
  Warning,
  X,
  CircleNotch
} from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import GeniLoader from "../components/GeniLoader";

export default function Referral() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [walletAddress, setWalletAddress] = useState("");
  const [savingWallet, setSavingWallet] = useState(false);
  const [enablingReferral, setEnablingReferral] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showWalletConfirm, setShowWalletConfirm] = useState(false);

  // Check if user has pending payouts (can't change wallet)
  const hasPendingPayouts = stats?.total_owed > 0;

  // Check for referral code in URL on mount
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      // Store in localStorage for later use during profile creation
      localStorage.setItem("referral_code", refCode.toUpperCase());
    }
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [statusRes, statsRes] = await Promise.all([
        api.get("/referral/status"),
        api.get("/referral/stats")
      ]);
      setStatus(statusRes.data);
      setStats(statsRes.data);
      setWalletAddress(statusRes.data.referral_wallet_address || "");
    } catch (error) {
      console.error("Error fetching referral data:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const handleEnableReferral = async () => {
    setEnablingReferral(true);
    try {
      const response = await api.post("/referral/enable");
      setStatus(prev => ({
        ...prev,
        referral_enabled: true,
        referral_code: response.data.referral_code
      }));
      toast.success("Referral program enabled!");
    } catch (error) {
      console.error("Error enabling referral:", error);
      toast.error("Failed to enable referral program");
    } finally {
      setEnablingReferral(false);
    }
  };

  // Open confirmation modal before saving
  const handleSaveWalletClick = () => {
    if (!walletAddress.trim()) {
      toast.error("Please enter a wallet address");
      return;
    }
    
    // Basic validation
    if (!walletAddress.trim().startsWith("0x") || walletAddress.trim().length !== 42) {
      toast.error("Invalid wallet address format. Must be 0x followed by 40 hex characters.");
      return;
    }
    
    setShowWalletConfirm(true);
  };

  // Actually save the wallet after confirmation
  const handleConfirmSaveWallet = async () => {
    setSavingWallet(true);
    setShowWalletConfirm(false);
    
    try {
      await api.put("/referral/wallet", { wallet_address: walletAddress.trim() });
      setStatus(prev => ({
        ...prev,
        referral_wallet_address: walletAddress.trim()
      }));
      toast.success("Wallet address saved!");
    } catch (error) {
      console.error("Error saving wallet:", error);
      const message = error.response?.data?.detail || "Failed to save wallet address";
      toast.error(message);
    } finally {
      setSavingWallet(false);
    }
  };

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/signup?ref=${status.referral_code}`;
    
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        toast.success("Referral link copied!");
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        // Clipboard API failed, try fallback
        console.warn("Clipboard API failed, trying fallback:", err);
      }
    }
    
    // Fallback: temporary textarea + execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success("Referral link copied!");
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      console.warn("Fallback copy also failed:", err);
    }
    
    // Both methods failed - show friendly message
    toast.error("Could not copy automatically. Please select and copy the link manually.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="referral-page">
      <div className="w-full max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black font-heading tracking-tight flex items-center gap-3">
            <Users size={32} weight="duotone" className="text-[#00E676]" />
            Referral Program
          </h1>
          <p className="text-white/60 mt-2">
            Invite traders and earn rewards when they upgrade to Pro.
          </p>
        </div>

        {/* Not Enabled State */}
        {!status?.referral_enabled && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
            <div className="w-20 h-20 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserPlus size={40} weight="duotone" className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold font-heading mb-3">Join the Referral Program</h2>
            <p className="text-white/60 mb-6 max-w-md mx-auto">
              Share your unique referral link and earn <span className="text-[#00E676] font-bold">10% commission</span> on every successful payment from users you refer.
            </p>
            <Button
              onClick={handleEnableReferral}
              disabled={enablingReferral}
              className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90 px-8 py-3"
              data-testid="enable-referral-btn"
            >
              {enablingReferral ? (
                <span className="flex items-center gap-2">
                  <CircleNotch size={18} className="animate-spin" />
                  Enabling...
                </span>
              ) : (
                "Join Referral Program"
              )}
            </Button>
          </div>
        )}

        {/* Enabled State */}
        {status?.referral_enabled && (
          <div className="space-y-6">
            {/* Stats Cards - 4 cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Successful Referrals */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={18} className="text-[#00E676]" />
                  <div className="text-white/40 text-xs font-mono">REFERRALS</div>
                </div>
                <div className="text-2xl font-bold font-mono" data-testid="total-referrals">
                  {stats?.successful_referrals || 0}
                </div>
                <p className="text-white/30 text-xs mt-1">Confirmed</p>
              </div>

              {/* Total Commission */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyDollar size={18} className="text-white/60" />
                  <div className="text-white/40 text-xs font-mono">COMMISSION</div>
                </div>
                <div className="text-2xl font-bold font-mono" data-testid="total-commission">
                  ${stats?.total_commission?.toFixed(2) || "0.00"}
                </div>
                <p className="text-white/30 text-xs mt-1">Total earned</p>
              </div>

              {/* Total Owed */}
              <div className="bg-[#121212] border border-[#FFC107]/30 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Hourglass size={18} className="text-[#FFC107]" />
                  <div className="text-white/40 text-xs font-mono">OWED</div>
                </div>
                <div className="text-2xl font-bold font-mono text-[#FFC107]" data-testid="total-owed">
                  ${stats?.total_owed?.toFixed(2) || "0.00"}
                </div>
                <p className="text-white/30 text-xs mt-1">Pending payout</p>
              </div>

              {/* Total Paid */}
              <div className="bg-[#121212] border border-[#00E676]/30 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={18} className="text-[#00E676]" />
                  <div className="text-white/40 text-xs font-mono">PAID</div>
                </div>
                <div className="text-2xl font-bold font-mono text-[#00E676]" data-testid="total-paid">
                  ${stats?.total_paid?.toFixed(2) || "0.00"}
                </div>
                <p className="text-white/30 text-xs mt-1">Received</p>
              </div>
            </div>

            {/* Wallet Address */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Wallet size={20} weight="duotone" className="text-white/60" />
                <h3 className="font-bold">USDT Wallet (Arbitrum)</h3>
              </div>
              
              {/* Blocked state warning */}
              {hasPendingPayouts && (
                <div className="flex items-start gap-3 p-3 mb-4 rounded-sm bg-[#FFC107]/10 border border-[#FFC107]/30">
                  <Warning size={18} className="text-[#FFC107] shrink-0 mt-0.5" />
                  <p className="text-[#FFC107] text-sm">
                    You cannot change your wallet while you have pending payouts.
                  </p>
                </div>
              )}
              
              <p className="text-white/40 text-sm mb-4">
                Enter your Arbitrum wallet address to receive referral payouts.
              </p>
              
              <div className="flex gap-3">
                <Input
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder="0x..."
                  disabled={hasPendingPayouts}
                  className={`bg-[#0A0A0A] border-white/10 text-white font-mono flex-1 ${hasPendingPayouts ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="wallet-input"
                />
                <Button
                  onClick={handleSaveWalletClick}
                  disabled={savingWallet || !walletAddress.trim() || hasPendingPayouts}
                  className="bg-white text-black font-bold hover:bg-gray-200 disabled:opacity-50"
                  data-testid="save-wallet-btn"
                >
                  {savingWallet ? (
                    <CircleNotch size={16} className="animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
              {status?.referral_wallet_address && (
                <div className="mt-3 flex items-center gap-2 text-xs text-[#00E676]">
                  <Check size={14} weight="bold" />
                  <span>Wallet saved</span>
                </div>
              )}
              
              {/* Hint text */}
              {!hasPendingPayouts && (
                <p className="mt-3 text-white/30 text-xs">
                  Payouts are sent to this wallet. Double-check before saving.
                </p>
              )}
            </div>

            {/* Referral Link */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <LinkIcon size={20} weight="duotone" className="text-white/60" />
                <h3 className="font-bold">Your Referral Link</h3>
              </div>
              
              <div className="mb-4">
                <div className="text-white/40 text-xs font-mono mb-2">REFERRAL CODE</div>
                <div className="text-2xl font-bold font-mono tracking-wider text-[#00E676]" data-testid="referral-code">
                  {status?.referral_code}
                </div>
              </div>

              <div className="flex gap-3">
                <input 
                  type="text"
                  readOnly
                  value={`${window.location.origin}/signup?ref=${status?.referral_code}`}
                  onClick={(e) => e.target.select()}
                  className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 font-mono text-sm text-white/80 truncate cursor-text focus:outline-none focus:border-white/20"
                  data-testid="referral-link-input"
                />
                <Button
                  onClick={copyReferralLink}
                  className={`${copied ? 'bg-[#00E676]' : 'bg-white'} text-black font-bold hover:bg-gray-200 px-6`}
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <span className="flex items-center gap-2">
                      <Check size={16} weight="bold" />
                      Copied
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Copy size={16} />
                      Copy
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Commission Info & Payout Notice */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-white/60 text-sm">
                  <span className="text-[#00E676] font-bold">10% commission</span> on every successful payment
                </p>
                <p className="text-white/40 text-xs">
                  Payouts processed monthly to your saved wallet
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Wallet Confirmation Modal */}
      {showWalletConfirm && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowWalletConfirm(false);
            }
          }}
        >
          <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Confirm Wallet Address</h3>
              <button
                onClick={() => setShowWalletConfirm(false)}
                className="text-white/40 hover:text-white p-1"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-5">
              <p className="text-white/80 mb-4">Are you sure this is your wallet?</p>
              
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 mb-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/40 text-sm">Network</span>
                  <span className="text-white font-mono text-sm">Arbitrum</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-white/40 text-sm">Address</span>
                  <span className="text-white font-mono text-sm break-all text-right max-w-[200px]">
                    {walletAddress}
                  </span>
                </div>
              </div>
              
              <div className="flex items-start gap-2 p-3 rounded-sm bg-[#FFC107]/10 border border-[#FFC107]/30">
                <Warning size={18} className="text-[#FFC107] shrink-0 mt-0.5" />
                <p className="text-[#FFC107] text-sm">
                  <strong>Important:</strong> You will NOT be able to change this wallet while you have pending payouts.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <Button
                onClick={() => setShowWalletConfirm(false)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSaveWallet}
                disabled={savingWallet}
                className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
              >
                {savingWallet ? (
                  <CircleNotch size={16} className="animate-spin" />
                ) : (
                  "Confirm"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
